import {
  createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode,
} from 'react'
import { explain } from '@/shared/api/problem'
import { listElections, type Election } from '@/features/elections/api'

/**
 * The selected election — one store, read by the shell and by every election-scoped page.
 *
 * ## Why this is chrome and not a filter
 *
 * A filter narrows a set. This DEFINES the set: a tally board, a candidate list, a seat
 * dashboard mean nothing until you say which election. That is why the selector lives in
 * the sub-bar beside "Viewing:" rather than in a filter row — it is closer to "which
 * database am I looking at" than to "show me only the critical ones".
 *
 * ## Why one store and not a picker per page
 *
 * `station-mates` already ships its own `ElectionPicker` with its own local state. Two
 * pickers that can disagree is a user reading 2027 numbers under a 2032 heading, and
 * nothing on screen says which one is stale. Anything election-scoped reads from here.
 *
 * ## Three places the selection lives, in priority order
 *
 * 1. **The URL** (`?election=<id>`) — wins, because a tally screen is a thing people send
 *    each other and a link that opens on the reader's own last selection is a link that
 *    lies about what it shows.
 * 2. **`localStorage`** — so a desk that reloads mid-count lands where it was.
 * 3. **Nothing** — and that is a real state the pages must render a sentence for, not an
 *    empty grid. It is also the honest state before any election has been created.
 *
 * It deliberately does NOT default to "the newest election". During a count that would
 * silently move somebody to a race they were not watching.
 */

const STORAGE_KEY = 'axis.election.selected'

interface ElectionContextValue {
  /** Every election the caller may see, newest polling date first. */
  elections: Election[]
  /** The selection, or null. Null is a real state — render a sentence, not an empty grid. */
  election: Election | null
  electionId: string | null
  select: (id: string | null) => void
  loading: boolean
  error: string | null
  reload: () => Promise<void>
}

const ElectionContext = createContext<ElectionContextValue | null>(null)

/** Read `?election=` without subscribing to the router — this provider sits above it. */
function urlElectionId(): string | null {
  if (typeof window === 'undefined') return null
  return new URLSearchParams(window.location.search).get('election')
}

function storedElectionId(): string | null {
  try {
    return window.localStorage.getItem(STORAGE_KEY)
  } catch {
    // A private window, or site data blocked. Not an error — just no memory.
    return null
  }
}

export function ElectionProvider({ children }: { children: ReactNode }) {
  const [elections, setElections] = useState<Election[]>([])
  const [selectedId, setSelectedId] = useState<string | null>(
    () => urlElectionId() ?? storedElectionId())
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const reload = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const rows = await listElections()
      rows.sort((a, b) => b.pollingDate.localeCompare(a.pollingDate))
      setElections(rows)
    } catch (err) {
      // A caller without `election:read` gets a refusal here. The selector says so; it does
      // not pretend there are no elections.
      setError(explain(err))
      setElections([])
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { void reload() }, [reload])

  /*
     A remembered id that no longer resolves — deleted, or outside this caller's scope —
     is dropped rather than left dangling. Keeping it would leave every page querying an
     election the server will 404, and the pages would each report that separately.
  */
  useEffect(() => {
    if (loading || !selectedId) return
    if (!elections.some((e) => e.id === selectedId)) setSelectedId(null)
  }, [loading, selectedId, elections])

  const select = useCallback((id: string | null) => {
    setSelectedId(id)
    try {
      if (id) window.localStorage.setItem(STORAGE_KEY, id)
      else window.localStorage.removeItem(STORAGE_KEY)
    } catch { /* storage unavailable — the selection still works for this tab */ }

    // Mirror into the URL without a router navigation: this provider wraps the router, and
    // `replaceState` keeps the link shareable without adding a history entry per change.
    try {
      const url = new URL(window.location.href)
      if (id) url.searchParams.set('election', id)
      else url.searchParams.delete('election')
      window.history.replaceState(window.history.state, '', url)
    } catch { /* nothing to mirror into */ }
  }, [])

  const value = useMemo<ElectionContextValue>(() => ({
    elections,
    election: elections.find((e) => e.id === selectedId) ?? null,
    electionId: elections.some((e) => e.id === selectedId) ? selectedId : null,
    select,
    loading,
    error,
    reload,
  }), [elections, selectedId, select, loading, error, reload])

  return <ElectionContext.Provider value={value}>{children}</ElectionContext.Provider>
}

export function useElectionContext(): ElectionContextValue {
  const ctx = useContext(ElectionContext)
  if (!ctx) {
    throw new Error('useElectionContext must be used inside <ElectionProvider>')
  }
  return ctx
}

/**
 * The id alone, for the common case.
 *
 * **List it in every dependency array that reads it.** Every page in this feature is
 * election-scoped, and a loader bound to `[]` binds to the null this returns on first
 * render — leaving the page permanently empty with nothing saying why. That defect shipped
 * on four station-mates pages and again on the incident wizard.
 */
export function useElectionId(): string | null {
  return useElectionContext().electionId
}
